import React from "react";
import { Toaster, toast as sonnerToast } from "sonner";
import "sonner/dist/styles.css";
import { CheckCircle, AlertCircle, Info, X } from "lucide-react";

interface ToastProviderProps {
  children?: React.ReactNode;
  position?:
    | "top-right"
    | "top-left"
    | "bottom-right"
    | "bottom-left"
    | "top-center"
    | "bottom-center";
  duration?: number;
}

const ToastProvider: React.FC<ToastProviderProps> = ({
  children,
  position = "top-right",
  duration = 5000,
}) => {
  return (
    <>
      <Toaster
        position={position}
        duration={duration}
        toastOptions={{
          classNames: {
            success: "bg-success-50 border-success-200 text-success-800",
            error: "bg-error-50 border-error-200 text-error-800",
            warning: "bg-warning-50 border-warning-200 text-warning-800",
            info: "bg-primary-50 border-primary-200 text-primary-800",
          },
        }}
        icons={{
          success: <CheckCircle className="h-5 w-5 text-success-500" />,
          error: <AlertCircle className="h-5 w-5 text-error-500" />,
          warning: <AlertCircle className="h-5 w-5 text-warning-500" />,
          info: <Info className="h-5 w-5 text-primary-500" />,
          close: <X className="h-4 w-4" />,
        }}
      />
      {children}
    </>
  );
};

export const toast = {
  success: (
    message: string,
    descriptionOrOptions?:
      | string
      | {
          description?: string;
          action?: { label: string; onClick: () => void };
          duration?: number;
        },
  ) => {
    return sonnerToast.success(
      message,
      typeof descriptionOrOptions === "string"
        ? { description: descriptionOrOptions }
        : descriptionOrOptions ?? {},
    );
  },
  error: (
    message: string,
    descriptionOrOptions?:
      | string
      | {
          description?: string;
          action?: { label: string; onClick: () => void };
          duration?: number;
        },
  ) => {
    return sonnerToast.error(
      message,
      typeof descriptionOrOptions === "string"
        ? { description: descriptionOrOptions }
        : descriptionOrOptions ?? {},
    );
  },
  warning: (
    message: string,
    descriptionOrOptions?:
      | string
      | {
          description?: string;
          action?: { label: string; onClick: () => void };
          duration?: number;
        },
  ) => {
    return sonnerToast.warning(
      message,
      typeof descriptionOrOptions === "string"
        ? { description: descriptionOrOptions }
        : descriptionOrOptions ?? {},
    );
  },
  info: (
    message: string,
    descriptionOrOptions?:
      | string
      | {
          description?: string;
          action?: { label: string; onClick: () => void };
          duration?: number;
        },
  ) => {
    return sonnerToast.info(
      message,
      typeof descriptionOrOptions === "string"
        ? { description: descriptionOrOptions }
        : descriptionOrOptions ?? {},
    );
  },
  loading: (message: string) => {
    return sonnerToast.loading(message);
  },
  promise: <T,>(
    promise: Promise<T>,
    msgs: {
      loading: string;
      success: string;
      error: string;
    },
  ) => {
    return sonnerToast.promise(promise, msgs);
  },
  message: (message: string, options?: Record<string, unknown>) => {
    return sonnerToast(message, options);
  },
  dismiss: (id?: string | number) => {
    return sonnerToast.dismiss(id);
  },
};

export { ToastProvider };
